import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api, getToken } from './api.js';
import { useI18n } from './i18n/index.jsx';
import { useLive } from './live.jsx';

// Website content edited in Admin > Website content (published version, or
// the admin's draft with ?preview=1). Every field falls back to the site's
// built-in text (i18n) when empty, so the site looks the same until
// something is published.

const ContentContext = createContext({ pages: {}, preview: false, ready: false });

const wantsPreview = () => {
  try { return new URLSearchParams(window.location.search).get('preview') === '1' && !!getToken(); } catch { return false; }
};

export function ContentProvider({ children }) {
  const [state, setState] = useState({ pages: {}, preview: false, ready: false });
  const [version, setVersion] = useState(0);
  // Published in Admin › Website content: read again, silently (live.jsx).
  useLive(['content'], () => setVersion((v) => v + 1));
  useEffect(() => {
    let alive = true;
    const preview = wantsPreview();
    // With a token, api() sends it (needed for previews); otherwise a plain public read.
    const load = preview ? api('/content?preview=1') : fetch('/api/content', { cache: 'no-cache' }).then((r) => (r.ok ? r.json() : Promise.reject(r)));
    load.then((d) => { if (alive) setState({ pages: d.pages || {}, preview: !!d.preview, ready: true }); })
      .catch(() => { if (alive) setState((s) => ({ ...s, ready: true })); });
    return () => { alive = false; };
  }, [version]);
  return <ContentContext.Provider value={state}>{children}</ContentContext.Provider>;
}

// const { text, page, preview } = useContent('home');
// text('title', 'home.title') → published text in the visitor's language,
// or the i18n default.
export function useContent(key) {
  const ctx = useContext(ContentContext);
  const { t, lang } = useI18n();
  const page = ctx.pages?.[key] || null;
  const text = useCallback((field, i18nKey, vars) => {
    const v = page?.[field];
    const s = v && typeof v === 'object' ? (v[lang] || '').trim() : '';
    if (s) return vars ? s.replace(/\{(\w+)\}/g, (m, k) => (vars[k] ?? m)) : s;
    return i18nKey ? t(i18nKey, vars) : '';
  }, [page, lang, t]);
  const value = useCallback((field, fallback = '') => {
    const v = page?.[field];
    return v === undefined || v === null || v === '' ? fallback : v;
  }, [page]);
  return useMemo(() => ({ page, text, value, preview: ctx.preview, ready: ctx.ready, lang }), [page, text, value, ctx.preview, ctx.ready, lang]);
}

// Bilingual value of an item (sectors list, posts): visitor's language,
// then the other one.
export const pickLang = (v, lang) => (v && typeof v === 'object' ? (v[lang] || v[lang === 'en' ? 'fr' : 'en'] || '') : (v || ''));

export function PreviewBanner() {
  const { preview } = useContext(ContentContext);
  const { t } = useI18n();
  if (!preview) return null;
  return <div className="preview-banner" role="status">👁 {t('content.previewBanner')}</div>;
}
